/**
 * product-social-proof-ticker.js
 * Social proof ticker — external ES module (automatically deferred, background-parsed).
 *
 * The snippet renders SSR initial content so the ticker is never blank.
 * This module takes over: builds the message pool, then rotates on an interval.
 *
 * Parsed off the main thread by V8's streaming parser.
 * Execution deferred to browser idle via requestIdleCallback.
 */

// ─── Data ────────────────────────────────────────────────────────────────────
// Reduced to 60 names — plenty of variety, ~70% less parse/allocation cost
const BUILTIN_NAMES = [
  'James','Emma','Oliver','Sophia','William','Ava','Liam','Isabella',
  'Benjamin','Mia','Lucas','Charlotte','Noah','Amelia','Mason','Harper',
  'Ethan','Evelyn','Logan','Abigail','Alexander','Emily','Jacob','Elizabeth',
  'Michael','Avery','Daniel','Sofia','Henry','Ella','Jackson','Madison',
  'Sebastian','Scarlett','Aiden','Victoria','Matthew','Aria','Samuel','Grace',
  'David','Chloe','Joseph','Camila','Carter','Penelope','Owen','Riley',
  'Wyatt','Layla','Jack','Nora','Luke','Zoey','Dylan','Aubrey',
  'Isaac','Hannah','Gabriel','Lily',
];

const BUILTIN_LOCATIONS = [
  'Austin, TX','Nashville, TN','Seattle, WA','Denver, CO','Boston, MA',
  'Portland, OR','Atlanta, GA','Miami, FL','Chicago, IL','San Francisco, CA',
  'Los Angeles, CA','New York, NY','San Diego, CA','Charlotte, NC','Dallas, TX',
  'Charleston, SC','London, UK','Edinburgh, UK','Dublin, IE','Paris, FR',
  'Berlin, DE','Amsterdam, NL','Sydney, AU','Melbourne, AU','Toronto, CA',
  'Vancouver, CA','Montreal, CA',
];

const ACTIONS = [
  '{name} from {city} just ordered this • {time}',
  '{name} from {city} added this to cart • {time}',
  '{name} from {city} just purchased • {time}',
  '{name} from {city} bought 2 of these • {time}',
  '{name} from {city} ordered the personalised version • {time}',
  '{name} from {city} just checked out with this • {time}',
  '{name} from {city} reordered this bag • {time}',
];

const GENERIC = [
  'Orders placed before 2 PM ship today',
  'Bestseller — back in stock recently',
  'Most gifted bag this season',
  'Free worldwide shipping on this order',
  'Rated 4.9/5 by verified buyers',
  'Personalised engraving available',
  'This colour is our fastest-selling variant',
  'Handcrafted — limited quantities per batch',
  'Our #1 gift choice for birthdays & anniversaries',
  'Full-grain leather — gets more beautiful with age',
  'Every purchase includes free dust bag & care card',
  'Rated 4.9★ by verified buyers',
];

// ─── Utilities ────────────────────────────────────────────────────────────────
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function randTime() {
  const m = pick([1,2,3,4,5,7,8,10,12,15,18,22,28,35,42]);
  return m === 1 ? 'just now' : m + ' mins ago';
}

function randNum(lo, hi) { return lo + Math.floor(Math.random() * (hi - lo + 1)); }

function generateSocialMsg(names, locations) {
  if (Math.random() < 0.7) {
    return pick(ACTIONS)
      .replace('{name}', pick(names))
      .replace('{city}', pick(locations))
      .replace('{time}', randTime());
  }
  return pick(GENERIC).replace('{sold}', randNum(180, 470));
}

function getLiveCount(min, max) {
  const hour = new Date().getHours();
  const weights = [.25,.2,.18,.15,.18,.22,.3,.45,.55,.7,.85,.95,1,.95,.85,.75,.7,.65,.7,.8,.9,.85,.6,.4];
  const base = Math.round(min + (max - min) * (weights[hour] || 0.5));
  return Math.max(min, Math.min(max, base + Math.floor(Math.random() * 7) - 3));
}

// ─── Seeded shuffle ──────────────────────────────────────────────────────────
function seededShuffle(arr, seed) {
  let s = seed;
  function sRand() { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; }
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(sRand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Core ticker init ─────────────────────────────────────────────────────────
function initTicker(cfgEl) {
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch(e) { return; }

  const sectionId  = cfgEl.id.replace('spt-data-', '');
  const bar        = document.getElementById('spt-bar-'  + sectionId);
  const textEl     = document.getElementById('spt-text-' + sectionId);
  if (!bar || !textEl) return;

  const names     = (cfg.globalNames     && cfg.globalNames[0]     !== '') ? cfg.globalNames     : BUILTIN_NAMES;
  const locations = (cfg.globalLocations && cfg.globalLocations[0] !== '') ? cfg.globalLocations : BUILTIN_LOCATIONS;

  // Build social pool
  let socialPool = [];
  if (cfg.metaMsgs && cfg.metaMsgs.length > 0) {
    socialPool = cfg.metaMsgs.slice();
  } else if (cfg.customMsgs && cfg.customMsgs.length > 0) {
    socialPool = cfg.customMsgs.slice();
  }
  const seen = Object.fromEntries(socialPool.map(m => [m, true]));
  let safety = 0;
  while (socialPool.length < 30 && safety < 200) {
    const m = generateSocialMsg(names, locations);
    if (!seen[m]) { socialPool.push(m); seen[m] = true; }
    safety++;
  }

  // Shuffle with session seed
  const seed = parseInt(sessionStorage.getItem('spt_seed'), 10) || Date.now();
  sessionStorage.setItem('spt_seed', seed);
  const shuffled = seededShuffle(socialPool, seed);

  // Interleave live entries
  const liveEvery = cfg.liveEvery || 3;
  const finalPool = [];
  shuffled.forEach((msg, i) => {
    finalPool.push({ type: 'social', msg });
    if ((i + 1) % liveEvery === 0) finalPool.push({ type: 'live' });
  });

  // Restore position from localStorage
  const idxKey = 'spt_idx_' + cfg.productId;
  let idx = parseInt(localStorage.getItem(idxKey), 10) || 0;
  if (idx >= finalPool.length) idx = 0;

  function applyEntry(entry) {
    if (entry.type === 'live') {
      textEl.textContent = getLiveCount(cfg.minCount, cfg.maxCount) + ' people are viewing this right now';
      bar.classList.add('is-live');
    } else {
      textEl.textContent = entry.msg;
      bar.classList.remove('is-live');
    }
  }

  // Apply first entry (replaces SSR content with a pool entry)
  applyEntry(finalPool[idx]);

  // Start rotation
  setInterval(() => {
    idx = (idx + 1) % finalPool.length;
    localStorage.setItem(idxKey, idx);

    textEl.classList.add('spt-out');
    setTimeout(() => {
      applyEntry(finalPool[idx]);
      textEl.classList.remove('spt-out');
      textEl.classList.add('spt-in');
      requestAnimationFrame(() => requestAnimationFrame(() => textEl.classList.remove('spt-in')));
    }, 320);
  }, cfg.interval);
}

// ─── Entry point ──────────────────────────────────────────────────────────────
// Find all ticker config blocks on this page (one per product page, but safe
// to handle multiple e.g. theme editor previews).
function run() {
  document.querySelectorAll('[id^="spt-data-"]').forEach(initTicker);
}

if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(run, { timeout: 2000 });
} else {
  setTimeout(run, 200);
}
